import {
	ApiClient,
	BeAppIds,
	FeClientIds,
	Logger,
	platformService,
} from '@monthio/react-components'
import { Subject, interval } from 'rxjs'

const logger = new Logger('LocalOverridesService')

interface OverrideStatus {
	appId: string
	overrideUp: boolean
}

const STOREAGE_KEY = 'MONTHIO:FLOW_LOCAL_OVERRIDES'

const ONE_MINUTE = 60e3

class LocalOverridesService {
	private interval = interval(ONE_MINUTE * 5)
	private subject = new Subject<OverrideStatus[]>()
	private client = new ApiClient({ baseUrl: '' })
	private overrides = new Map<
		string,
		{ overrideUrl: string; checkPath: string }
	>()
	public initialize() {
		this.setOverridesFromStorage()
		this.updateStatus()
		this.interval.subscribe(() => {
			this.updateStatus()
		})
	}

	public updateStatus = async () => {
		const result = await this.checkUrl()
		logger.log(result)
		const status = result
			.map((s) => {
				if (s.status === 'fulfilled') {
					return s.value
				}
				return
			})
			.filter(Boolean) as OverrideStatus[]

		this.subject.next(status)
	}

	public enableOverride(
		appId: BeAppIds | FeClientIds,
		overrideUrl: string,
		checkPath = '',
	) {
		this.overrides.set(appId, { overrideUrl, checkPath })
		platformService.overrideAppUrl(appId, overrideUrl)
		this.saveOverrides()
		this.updateStatus()
	}

	public disableOverride(appId: BeAppIds | FeClientIds) {
		this.overrides.delete(appId)
		platformService.resetAppUrl(appId)
		this.saveOverrides()
		this.updateStatus()
	}

	private saveOverrides() {
		window.localStorage.setItem(
			STOREAGE_KEY,
			JSON.stringify(Array.from(this.overrides)),
		)
	}

	public get update() {
		return this.subject
	}

	public resetLocalOverrides = () => {
		window.localStorage.removeItem(STOREAGE_KEY)
		this.overrides.clear()
		const configs = platformService.getConfigs()
		configs.apps.forEach((app) => platformService.resetAppUrl(app.id as any))
		configs.clients.forEach((app) => platformService.resetAppUrl(app.id as any))
		this.updateStatus()
	}

	private setOverridesFromStorage() {
		try {
			const overrides: [
				BeAppIds | FeClientIds,
				{ checkPath: string; overrideUrl: string },
			][] = JSON.parse(window.localStorage.getItem(STOREAGE_KEY) ?? '[]')

			overrides?.forEach(([appId, { checkPath, overrideUrl }]) => {
				this.overrides.set(appId, { checkPath, overrideUrl })
				platformService.overrideAppUrl(appId, overrideUrl)
			})
		} catch (error) {}

		return
	}

	private async checkUrl() {
		return Promise.allSettled<OverrideStatus>(
			Array.from(this.overrides).map(
				async ([appId, { checkPath, overrideUrl }]) => {
					const result = await this.client
						.silentFail()
						.timeout(1000)
						.getRaw(`${overrideUrl}${checkPath}`)
						.text()
						.then(() => true)
						.catch(() => false)

					logger.info(appId, `${overrideUrl}${checkPath}`, result)
					return {
						appId,
						overrideUp: result,
					}
				},
			),
		)
	}
}

export const localOverridesService = new LocalOverridesService()
